import {
  ChatBubbleBottomCenterTextIcon,
  CheckIcon,
  DocumentPlusIcon,
  VideoCameraIcon,
} from '@heroicons/react/24/solid';
import React, { useContext, useMemo, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useNavigate, useParams } from 'react-router-dom';

import CheckListIcon from 'assets/CheckListIcon';
import Button, { ButtonVariant } from 'components/Button/Button';
import CreateCompetencyModal from 'components/CreateCompetencyModal/CreateCompetencyModal';
import DraggableCompetencies from 'components/DraggableCompetencies/DraggableCompetencies';
import StepCard from 'components/StepCard/StepCard';
import WelcomeBanner, {
  WelcomeBannerVariant,
} from 'components/WelcomeBanner/WelcomeBanner';
import useGetAssessmentDetails from 'hooks/useGetAssessmentDetails';
import useGetAssessmentSteps, {
  CompetencyType,
  ICompetency,
} from 'hooks/useGetAssessmentSteps';
import { t } from 'locales/i18n';
import AdditionalStepCard from 'pages/AllSteps/AdditionalStepCard';
import MetaStats from 'pages/AllSteps/MetaStats';
import MetaWrapper from 'pages/AllSteps/MetaWrapper';
import { AssessmentDetailsContext } from 'providers/AssessmentDetailsProvider';
import { secondsToTime } from 'utils/secondsToTime';

const AllSteps = () => {
  const navigate = useNavigate();
  const { isPractice } = useContext(AssessmentDetailsContext);
  const [isOpen, setIsOpen] = useState(false);
  const { interviewUid } = useParams();
  const { data: interview, isLoading: isAssessmentDetailsLoading } =
    useGetAssessmentDetails(interviewUid ?? '');
  const { data: assessmentSteps, isLoading: isAssessmentStepsLoading } =
    useGetAssessmentSteps(interviewUid ?? '');

  const calculateQuestions = (competencies: ICompetency[] | undefined) => {
    if (!competencies) {
      return 0;
    }
    return competencies.filter(
      (competency) =>
        competency.type !== CompetencyType.PASSIVE && competency.questions > 0
    ).length;
  };

  const {
    welcome_video: welcomeVideo,
    registration,
    assessment,
    supplemental_materials: supplementalMaterials,
    device_setup: deviceSetup,
    practice,
    payment,
    closing_video: closingVideo,
  } = {
    ...assessmentSteps?.steps,
  };

  const steps = [
    {
      priority: 1,
      title: t('Welcome Video'),
      action: () => navigate('../welcome-video'),
      isShowing: welcomeVideo?.active,
      thumbnail: welcomeVideo?.thumbnail,
      metadata: <MetaWrapper name={welcomeVideo?.file_type?.toUpperCase()} />,
      dataTestId: 'welcome-video',
    },
    {
      priority: 2,
      title: t('Registration'),
      action: () => navigate('../registration'),
      isShowing: registration?.active,
      metadata: (registration?.has_student_id_requirement ||
        registration?.has_cas_id_requirement) && (
        <div className="flex items-center gap-6">
          {registration?.has_student_id_requirement && (
            <MetaWrapper name={t('Student ID')} icon={<CheckIcon />} />
          )}
          {registration?.has_cas_id_requirement && (
            <MetaWrapper name={t('CAS ID')} icon={<CheckIcon />} />
          )}
        </div>
      ),
      dataTestId: 'registration',
    },
    {
      priority: 3,
      title: t('Supplemental Materials'),
      action: () => navigate('../supplemental-materials'),
      isShowing: supplementalMaterials?.active,
    },
    {
      priority: 4,
      title: t('Device Setup'),
      action: () => navigate('../device-setup'),
      isShowing: deviceSetup?.active,
    },
    {
      priority: 5,
      title: t('Practice'),
      action: () => navigate('../practice'),
      isShowing: practice?.active,
      headerContent: (
        <div className="flex gap-8 text-right">
          <MetaStats
            name={t('Question Group', {
              count: practice?.competencies?.length || 0,
            })}
            value={practice?.competencies?.length || 0}
          />
          <MetaStats
            name={t('Question', {
              count: calculateQuestions(practice?.competencies),
            })}
            value={calculateQuestions(practice?.competencies)}
          />
          <MetaStats
            name={t('Duration')}
            value={secondsToTime(practice?.duration || 0)}
          />
        </div>
      ),
      metadata: practice?.name && (
        <MetaWrapper name={practice?.name} icon={<CheckIcon />} />
      ),
    },
    {
      priority: 6,
      title: t('Payment'),
      action: () => navigate('../payment'),
      isShowing: payment?.active,
    },
    {
      priority: 7,
      title: isPractice ? t('Practice') : t('Assessment'),
      isShowing: assessment?.active,
      containerClassNames: '!items-baseline',
      headerClassNames: 'border-b pb-2',
      stepCardClassNames: 'cursor-default md:!pt-4',
      showOnPractice: true,
      dataTestId: 'assessment',
      headerContent: (
        <div className="flex gap-8 text-right">
          <MetaStats
            name={
              isPractice
                ? t('Question Group', {
                    count: assessment?.competencies?.length || 0,
                  })
                : t('Competency', {
                    count: assessment?.competencies?.length || 0,
                  })
            }
            value={assessment?.competencies?.length || 0}
          />
          <MetaStats
            name={t('Question', {
              count: calculateQuestions(assessment?.competencies),
            })}
            value={calculateQuestions(assessment?.competencies)}
          />
          <MetaStats
            name={t('Duration')}
            value={secondsToTime(assessment?.duration || 0)}
          />
        </div>
      ),
      child:
        assessment?.competencies && assessment?.competencies?.length > 0 ? (
          <>
            <DraggableCompetencies
              competencies={assessment?.competencies}
              interviewUid={interviewUid ?? ''}
            />
            <Button
              dataTestId="addCompetency"
              variant={ButtonVariant.SECONDARY}
              buttonClassNames="self-start"
              buttonText={
                isPractice ? t('Add Question Group') : t('Add Competency')
              }
              onClick={() => setIsOpen(true)}
            />
          </>
        ) : (
          <div className="item-center flex w-full flex-col justify-center gap-4 p-8">
            <div className="flex justify-center">
              <CheckListIcon />
            </div>
            <div className="prose-title-h4">
              {isPractice
                ? t('No question groups added yet')
                : t('No competencies added yet')}
            </div>
            <Button
              dataTestId="addCompetency"
              variant={ButtonVariant.SECONDARY}
              buttonText={
                isPractice ? t('Add Question Group') : t('Add Competency')
              }
              onClick={() => setIsOpen(true)}
            />
          </div>
        ),
    },
    {
      priority: 8,
      title: t('Closing Video'),
      action: () => navigate('../closing-video'),
      isShowing: closingVideo?.active,
      thumbnail: closingVideo?.thumbnail,
      dataTestId: 'closing-video',
      metadata: <MetaWrapper name={closingVideo?.file_type?.toUpperCase()} />,
    },
  ];

  const additionalSteps = [
    {
      priority: 1,
      title: t('Welcome Video'),
      description: t(
        'Welcome your applicants with a video that showcases your school, campus, and program.'
      ),
      tag: t('Recommended'),
      icon: <VideoCameraIcon />,
      action: () => navigate('../welcome-video'),
      isShowing: !welcomeVideo?.active,
      dataTestId: 'welcome-video',
    },
    {
      priority: 2,
      title: t('Closing Video'),
      description: t(
        'Wrap up your assessment experience with a few closing remarks.'
      ),
      tag: t('Recommended'),
      icon: <VideoCameraIcon />,
      action: () => navigate('../closing-video'),
      isShowing: !closingVideo?.active,
      dataTestId: 'closing-video',
    },
    {
      priority: 3,
      title: t('Supplemental Materials'),
      description: t(
        'Require applicants to upload and/or download documents during Check In.'
      ),
      icon: <DocumentPlusIcon />,
      action: () => navigate('../supplemental-materials'),
      isShowing: !supplementalMaterials?.active,
      dataTestId: 'supplementalMaterials',
    },
    {
      priority: 4,
      title: t('Practice'),
      description: t(
        'Allow applicants to familiarize themselves with the platform by practicing with questions of your choice.'
      ),
      tag: t('Recommended'),
      icon: <ChatBubbleBottomCenterTextIcon />,
      action: () => navigate('../practice'),
      isShowing: !practice?.active,
      dataTestId: 'practice',
    },
  ];

  const filteredSteps = useMemo(
    () =>
      steps.filter((step) =>
        isPractice ? step.showOnPractice : step.isShowing
      ),
    [steps]
  );

  const filteredAdditionalSteps = useMemo(
    () => additionalSteps.filter((step) => step.isShowing === true),
    [additionalSteps]
  );

  const isAssessmentPristine =
    filteredAdditionalSteps.length === additionalSteps.length &&
    assessment?.competencies.length === 0 &&
    !registration?.has_student_id_requirement &&
    !registration?.has_cas_id_requirement;

  return (
    <div className="m-8 flex flex-col gap-6">
      {isAssessmentPristine && (
        <WelcomeBanner
          title={
            isPractice
              ? t('Welcome to your practice!')
              : t('Welcome to your new assessment!')
          }
          description={
            !isPractice
              ? t("We've added a few steps to help you get started.")
              : undefined
          }
          variant={WelcomeBannerVariant.WELCOME}
        />
      )}
      {!isAssessmentDetailsLoading && !isAssessmentStepsLoading ? (
        <>
          <div className="ml-12 flex gap-2">
            <div className="prose-title-h2">{interview?.name}</div>
            <div className="prose-body-xlg-reg text-kira-slate-700">{`${
              filteredSteps?.length
            } ${t('Step', { count: filteredSteps?.length })}`}</div>
          </div>
          <div className="flex flex-col gap-4">
            {filteredSteps.map((step, index) => (
              <StepCard
                key={step.priority}
                stepNumber={index + 1}
                title={step.title}
                action={step.action}
                isShowing={step.isShowing}
                headerContent={step.headerContent}
                headerClassNames={step.headerClassNames}
                metadata={step.metadata}
                stepCardClassNames={step.stepCardClassNames}
                containerClassNames={step.containerClassNames}
                thumbnail={step?.thumbnail}
                dataTestId={step?.dataTestId}
              >
                {step.child ? step.child : null}
              </StepCard>
            ))}
          </div>
        </>
      ) : (
        <div className="ml-12 grid gap-6">
          <Skeleton height={60} />
          <Skeleton height={100} />
          <Skeleton height={100} />
          <Skeleton height={100} />
        </div>
      )}

      {!isAssessmentStepsLoading &&
      filteredAdditionalSteps.length > 0 &&
      !isPractice ? (
        <div className="ml-12 mt-4 flex flex-col">
          <div className="prose-title-h2">{t('Additional Steps')}</div>
          <div className="prose-body-med-reg mt-1">
            {t('Add additional steps to your assessment.')}
          </div>
          <div className="mt-6 grid gap-6 lg:grid-cols-2">
            {filteredAdditionalSteps.map((step) => (
              <AdditionalStepCard
                key={step.priority}
                title={step.title}
                description={step.description}
                icon={step.icon}
                tag={step.tag}
                action={step.action}
                isShowing={step.isShowing}
                dataTestId={step?.dataTestId}
              />
            ))}
          </div>
        </div>
      ) : null}
      <CreateCompetencyModal isOpen={isOpen} setIsOpen={setIsOpen} />
    </div>
  );
};

export default AllSteps;
